import { SubscriptionStatus } from "../types";
import { holdsBilling, isAdminTenantType } from "./tenantAccess";
import { awaitingCheckout, signupOpen } from "./signupIntent";
import { formatDate } from "./format";

// ENG-2676 — who may reach the Stripe billing portal.
//
// Pure and React-free for the same reason `adminGate` is: this is the escape hatch out of a lapsed
// subscription, and every branch of it needs to be assertable without standing up a render.
//
// The rule this replaces was `hasSubscription && (role === 'owner' || role === 'admin')`, written
// with the explicit intent that it stay reachable "even [with] a lapsed one". It could not.
// `hasSubscription` is `subscription != null` where the subscription comes from the backend's
// `getActiveByTenantId`, which filters `status IN ('active','trialing')` — so it is FALSE for a
// cancelled or past-due licence, which is the only case the block exists for. The other billing
// entry (Members & Seats) sits behind `canManageMembers`, which needs an active licence too, so an
// owner of a lapsed org had no route to Stripe anywhere in the app. Our own subscription sat
// cancelled for seven weeks partly because of that.
//
// The gate is therefore identity plus evidence-of-a-subscription, never current billing health:
// billing must be reachable PRECISELY when billing is unhealthy.

/**
 * ENG-3412 — does this person pay for a personal subscription of their own?
 *
 * A legacy personal subscriber has no organization, so the response describing them carries no role
 * or tenant type and the organization test in `canReachBilling` below read "no": they were billed through
 * Stripe with no way to reach the portal from here, and Settings told them Haven cost them nothing.
 * The backend flags that response explicitly rather than giving it a role, which would make the rest
 * of the console treat them as an organization member.
 */
export const paysForPersonalSubscription = (status: SubscriptionStatus | null | undefined): boolean =>
  status?.personalSubscription === true;

export { holdsBilling };

/**
 * May this person open the billing portal?
 *
 * `status != null` is the "has a subscription, even a lapsed one" test — it works only because
 * ENG-2676 made the backend report the latest subscription's status rather than the active-only one,
 * so a cancelled org now answers `"cancelled"` where it used to answer nothing.
 *
 * Excluding the null case is deliberate rather than incidental: a managed tenant that has never had
 * a subscription (a seed or demo fixture) has no Stripe customer behind it, so the portal call would
 * fail. Offering a button that can only error is its own dead end, and harder to diagnose than an
 * absent one.
 *
 * ENG-3412: or someone who pays for a personal subscription, whose portal is their own.
 */
export const canReachBilling = (status: SubscriptionStatus | null | undefined): boolean =>
  paysForPersonalSubscription(status)
  || (!!status
    && holdsBilling(status.role, status.tenantType)
    && isAdminTenantType(status.tenantType)
    && status.status != null);

/**
 * ENG-3433 — is this organization's billing handled by Haven, so there is no Stripe portal to open?
 *
 * A comped or hand-built subscription has no Stripe id (`seatsBilled === false`) yet reads `active`,
 * so `canReachBilling` offers it the portal. With the org named (ENG-3421),
 * `POST /create-billing-portal-session` finds no Stripe subscription for it and answers 404 on every
 * click. Where this is true the console says Haven handles the billing instead of offering the button.
 *
 * Only for an active subscription (`hasSubscription`). `seatsBilled` is read off the active one and is
 * null without it, so a lapsed organization keeps the button, which is its way out. Absent (a backend
 * older than ENG-3340) is unknown, and keeps the button too.
 */
export const billingHandledByHaven = (status: SubscriptionStatus | null | undefined): boolean =>
  !!status && status.hasSubscription && status.seatsBilled === false;

/**
 * ENG-3851, ENG-3181: does the person who bills this family or business pay through checkout rather than the portal?
 *
 * An organization that never paid has no portal to pay in (ENG-3244), and a cancelled Stripe subscription
 * cannot be restarted from it. An `expired` one (Stripe's `unpaid`) still exists and its invoice can be
 * paid in the portal, so it keeps the portal: checking out instead would leave it beside a new one.
 * Needs `selfServeSignup`, which checkout refuses without.
 */
export const renewsByCheckout = (status: SubscriptionStatus | null | undefined): boolean =>
  awaitingCheckout(status) && signupOpen(status) && (status?.status == null || status.status === "cancelled");

/**
 * Is this subscription in a state the owner should be told to act on?
 *
 * `trialing` is not one: the backend normalises it to `active` at write time
 * (`StripeService` — "treat trialing as active so users get full access"), so it does not reach the
 * client. Checked against `active` rather than a list for that reason — adding `trialing` here would
 * imply the client sees a state it never does.
 */
export const needsBillingAttention = (status: SubscriptionStatus | null | undefined): boolean =>
  !!status && status.status !== "active";

/** The status in words. Falls back only when the backend genuinely has nothing to report. */
export const describeBillingStatus = (raw: string | null | undefined): string =>
  raw === "past_due" ? "past due" : (raw ?? "inactive");

/**
 * ENG-3421 — the tenant a billing call names in `X-Haven-Active-Tenant`, or null to name none.
 *
 * The organization this page is showing, whether or not the admin ever used the switcher. Without the
 * header the backend picks an organization of its own: the portal took the most recently joined org
 * the caller administers that has an active Stripe subscription. So an owner of a lapsed org who also
 * had a licensed one got the licensed one's portal, and an owner of several got whichever they joined
 * last (the ENG-3032 class). Add-seats picks by its own rule too. Named, the backend acts on exactly
 * this tenant or refuses.
 *
 * Null sends no header, and the backend resolves the caller's own billing as before. That is:
 *  - a personal account, or a member, who has no organization billing to name;
 *  - [mayNameTenant] false: a sign-in the backend refuses any header from (`signInMayNameTenant`).
 *    Naming a tenant would turn a password user's working portal into a 403.
 */
export const billingTenantId = (
  status: SubscriptionStatus | null | undefined,
  mayNameTenant: boolean,
): string | null =>
  mayNameTenant && !!status && holdsBilling(status.role, status.tenantType) && isAdminTenantType(status.tenantType)
    ? status.tenantId ?? null
    : null;

/**
 * ENG-3905: what Stripe has scheduled (a trial end, a cancel), which the portal shows and Billing
 * otherwise would not. Only a Business plan's members lose Haven when it ends, so only it says so.
 * ENG-4112: and when a promotion ends, unless a cancel ends the plan first.
 */
export function scheduledBillingNotice(status: SubscriptionStatus): string | null {
  const ends = status.cancelAt != null ? formatDate(status.cancelAt) : null;
  const trialEndsAt = status.tenantType === "family" ? status.trialEndsAt ?? null : null;
  const promo = status.discountEndsAt != null && (trialEndsAt == null || status.discountEndsAt > trialEndsAt)
    ? formatDate(status.discountEndsAt)
    : null;
  if (!ends && promo) {
    // A partial code keeps the trial (ENG-4083), and an older subscription can have a 100% one with it,
    // so the trial end is a charge at the promotion's price, which can be $0.
    if (trialEndsAt != null) {
      return `Your free trial ends on ${formatDate(trialEndsAt)}. After that, Haven Family renews automatically at your promotion's price until ${promo}, then at the regular price. You can cancel anytime in Manage billing.`;
    }
    const plan = status.tenantType === "family" ? "Haven Family" : "your plan";
    return `Your promotion ends on ${promo}. After that, ${plan} renews at the regular price. You can cancel anytime in Manage billing.`;
  }
  if (trialEndsAt != null) {
    const trial = formatDate(trialEndsAt);
    if (!ends) return `Your free trial ends on ${trial}. After that, Haven Family renews automatically. You can cancel anytime in Manage billing.`;
    // A cancel set past the trial end (dashboard or API) means the plan renews first.
    return status.cancelAt! <= trialEndsAt
      ? `Your free trial ends on ${trial}, and your plan will end with it. To keep it, open Manage billing and renew.`
      : `Your free trial ends on ${trial}. Your plan ends on ${ends}. To keep it, open Manage billing and renew.`;
  }
  if (!ends) return null;
  return status.tenantType === "business"
    ? `Your plan ends on ${ends}. Members keep Haven until then, and lose it after. To keep it, open Manage billing and renew.`
    : `Your plan ends on ${ends}. To keep it, open Manage billing and renew.`;
}
